import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, X, Rocket, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function OnboardingChecklist() {
  const [data, setData] = useState(null);
  const nav = useNavigate();
  const load = () => api.get("/onboarding").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const dismiss = async () => { await api.post("/onboarding/dismiss"); setData(null); };

  if (!data || data.dismissed) return null;
  const pct = Math.round((data.completed / data.total) * 100);

  if (data.all_done) {
    if (localStorage.getItem("pk_onboard_celebrated") === "1") return null;
    return (
      <Card className="mb-6 border-0 shadow-none overflow-hidden text-white text-center" style={{ background: "linear-gradient(135deg,#059669,#4f46e5)" }} data-testid="onboarding-celebration">
        <div className="p-8">
          <div className="text-4xl mb-2">🎉</div>
          <h3 className="font-display font-bold text-2xl">You're all set!</h3>
          <p className="text-sm text-white/80 mt-1 max-w-md mx-auto">Your AI caller is ready to qualify leads 24/7. Nice work getting everything connected.</p>
          <Button variant="secondary" className="mt-4" onClick={() => { localStorage.setItem("pk_onboard_celebrated", "1"); setData(null); }} data-testid="celebration-dismiss">Let's go</Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="mb-6 border-0 shadow-none overflow-hidden text-white" style={{ background: "linear-gradient(135deg,#4f46e5,#7c3aed)" }} data-testid="onboarding-checklist">
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center"><Rocket className="w-5 h-5" /></div>
            <div>
              <h3 className="font-display font-bold text-lg">Let's get you live</h3>
              <p className="text-sm text-white/70">{data.completed} of {data.total} steps done — finish setup to start qualifying leads 24/7.</p>
            </div>
          </div>
          <button onClick={dismiss} className="text-white/60 hover:text-white" data-testid="onboarding-dismiss"><X className="w-4 h-4" /></button>
        </div>
        <div className="h-1.5 rounded-full bg-white/20 mt-4 mb-4"><div className="h-1.5 rounded-full bg-white transition-all" style={{ width: `${pct}%` }} /></div>
        <div className="grid md:grid-cols-2 gap-2">
          {data.steps.map((s) => (
            <button key={s.key} onClick={() => nav(s.path)} disabled={s.done}
              className={`flex items-center gap-3 text-left px-3 py-2.5 rounded-xl transition-colors ${s.done ? "bg-white/5 cursor-default" : "bg-white/10 hover:bg-white/20"}`}
              data-testid={`onboarding-step-${s.key}`}>
              {s.done ? <CheckCircle2 className="w-5 h-5 text-emerald-300 shrink-0" /> : <Circle className="w-5 h-5 text-white/50 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${s.done ? "line-through text-white/60" : ""}`}>{s.label}</div>
                {!s.done && <div className="text-[11px] text-white/60 truncate">{s.desc}</div>}
              </div>
              {!s.done && <span className="inline-flex items-center gap-1 text-xs font-semibold shrink-0">{s.cta}<ArrowRight className="w-3 h-3" /></span>}
            </button>
          ))}
        </div>
      </div>
    </Card>
  );
}
